import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import "./ThunderWriter.css";

/*
 * Promo for Thunder Writer, Marc's free book-writing app.
 * The logo, demo clips and posters live in public/thunder-writer. The clips are
 * 960x600 re-encodes of the 1920x1200 originals in the thunder-writer repo
 * (src/assets/brand), and only the selected one ever loads.
 */
const APP_URL = "https://www.thunderwriter.app";
const ASSETS = `${process.env.PUBLIC_URL}/thunder-writer`;
const LOGO = `${ASSETS}/logo-192.webp`;

// The TV screen is ~225x165 on phones, so there the promo opens as a sheet instead.
// Short windows (phones held sideways) get the sheet too.
const SHEET_QUERY = "(max-width: 768px), (max-height: 560px)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const PROOF = ["Free", "No account", "Runs in your browser"];

const DEMOS = [
    {
        id: "ai-suggestions",
        icon: "sparkles",
        label: "AI ideas",
        title: "AI suggestions",
        caption: "Pause, and a suggestion or two appears in the margin. Accept it, decline it or hide it.",
    },
    {
        id: "book-preview",
        icon: "book",
        label: "Preview",
        title: "Book Preview",
        caption: "Your manuscript as a printed book, with spreads, running heads and page turns.",
    },
    {
        id: "chapters-new-page",
        icon: "bookmark",
        label: "Chapters",
        title: "Chapters",
        caption: "Every chapter starts on a fresh page, at a real trim size like a 6 × 9 paperback.",
    },
    {
        id: "focus-mode",
        icon: "focus",
        label: "Focus",
        title: "Focus mode",
        caption: "Focus Mode clears away everything but your pages.",
    },
];

const FEATURES = [
    {
        icon: "sparkles",
        title: "AI suggestions",
        text: "One or two ideas at a time, from your own Claude, OpenAI or OpenRouter key. Nothing changes until you accept.",
    },
    {
        icon: "book",
        title: "Book Preview",
        text: "Turn the pages of your book the way a reader will, then print it or save a PDF.",
    },
    {
        icon: "bookmark",
        title: "Chapters",
        text: "Real trim sizes, and every chapter opens on a new page.",
    },
    {
        icon: "focus",
        title: "Focus mode",
        text: "Hide everything but your pages. Typewriter sounds if you like them.",
    },
    {
        icon: "file",
        title: "Word & PDF",
        text: "Open and save Word, PDF, RTF, Markdown and more.",
    },
    {
        icon: "shield",
        title: "Private by design",
        text: "No account. Your book stays in your browser and your own Google Drive.",
    },
];

// Inline icons (24px grid, drawn with currentColor) so no icon library is needed
const ICONS = {
    bolt: <path d="M13.5 2 4.5 13.5h6.5L10 22l9.5-12H13l.5-8Z" fill="currentColor" stroke="none" />,
    sparkles: (
        <>
            <path d="M11 4.5 12.9 9l4.6 1.9-4.6 1.9L11 17.4l-1.9-4.6-4.6-1.9L9.1 9 11 4.5Z" />
            <path d="M19 15v5M16.5 17.5h5M18 3v3M16.5 4.5h3" />
        </>
    ),
    book: (
        <>
            <path d="M12 6.5C10.3 5.2 8 4.5 5 4.5H3v14h2c3 0 5.3.7 7 2 1.7-1.3 4-2 7-2h2v-14h-2c-3 0-5.3.7-7 2Z" />
            <path d="M12 6.5v14" />
        </>
    ),
    bookmark: (
        <>
            <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4.5L5 21V4a1 1 0 0 1 1-1Z" />
            <path d="M9 8h6" />
        </>
    ),
    focus: (
        <>
            <path d="M4 8.5V5a1 1 0 0 1 1-1h3.5M15.5 4H19a1 1 0 0 1 1 1v3.5M20 15.5V19a1 1 0 0 1-1 1h-3.5M8.5 20H5a1 1 0 0 1-1-1v-3.5" />
            <circle cx="12" cy="12" r="3" />
        </>
    ),
    file: (
        <>
            <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
            <path d="M14 3v5h5M12 11v6M9.5 14.5 12 17l2.5-2.5" />
        </>
    ),
    shield: (
        <>
            <path d="M12 3 5 6v5c0 4.5 3 8.3 7 10 4-1.7 7-5.5 7-10V6l-7-3Z" />
            <path d="m9 12 2 2 4-4" />
        </>
    ),
    check: <path d="m5 12.5 4.5 4.5L19 7.5" />,
    external: <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />,
    play: <path d="M8 5.5v13l10.5-6.5L8 5.5Z" fill="currentColor" />,
    pause: (
        <>
            <rect x="6.5" y="5" width="4" height="14" rx="1" fill="currentColor" stroke="none" />
            <rect x="13.5" y="5" width="4" height="14" rx="1" fill="currentColor" stroke="none" />
        </>
    ),
    close: <path d="M6 6l12 12M18 6 6 18" />,
};

function ThunderIcon({ name, className }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
        >
            {ICONS[name]}
        </svg>
    );
}

function useMediaQuery(query) {
    const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

    useEffect(() => {
        const mql = window.matchMedia(query);
        const onChange = () => setMatches(mql.matches);
        onChange();
        mql.addEventListener("change", onChange);
        return () => mql.removeEventListener("change", onChange);
    }, [query]);

    return matches;
}

function DemoVideo({ demo, reduceMotion }) {
    const videoRef = useRef(null);
    // Plays muted and looping, only while on screen, and never on its own under reduced motion
    const [paused, setPaused] = useState(reduceMotion);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!("IntersectionObserver" in window)) {
            setVisible(true);
            return undefined;
        }
        const observer = new IntersectionObserver(
            ([entry]) => setVisible(entry.isIntersecting),
            { threshold: 0.3 }
        );
        observer.observe(videoRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        if (!visible || paused) {
            video.pause();
            return;
        }
        video.muted = true;
        const playing = video.play();
        if (playing) {
            // Autoplay can still be refused (e.g. iOS Low Power Mode): fall back to the play button
            playing.catch((err) => {
                if (err.name === "NotAllowedError") setPaused(true);
            });
        }
    }, [visible, paused]);

    return (
        <figure className="thunder-video">
            <div className="thunder-video-frame">
                <video
                    ref={videoRef}
                    src={`${ASSETS}/${demo.id}.mp4`}
                    poster={`${ASSETS}/${demo.id}-poster.webp`}
                    width="960"
                    height="600"
                    muted
                    loop
                    playsInline
                    preload="none"
                    aria-label={`${demo.title} in Thunder Writer`}
                />
                <button
                    type="button"
                    className="thunder-video-toggle"
                    onClick={() => setPaused((p) => !p)}
                    aria-label={`${paused ? "Play" : "Pause"} the ${demo.title} demo`}
                >
                    <ThunderIcon name={paused ? "play" : "pause"} />
                </button>
            </div>
            <figcaption>{demo.caption}</figcaption>
        </figure>
    );
}

function DemoPlayer({ reduceMotion }) {
    const [activeId, setActiveId] = useState(DEMOS[0].id);
    const demo = DEMOS.find((d) => d.id === activeId);

    return (
        <section className="thunder-demo" aria-labelledby="thunder-demo-title">
            <h2 className="thunder-kicker" id="thunder-demo-title">See it in action</h2>
            <div className="thunder-tabs" role="group" aria-label="Choose a demo">
                {DEMOS.map((d) => (
                    <button
                        key={d.id}
                        type="button"
                        className={`thunder-tab ${d.id === activeId ? "active" : ""}`}
                        aria-pressed={d.id === activeId}
                        onClick={() => setActiveId(d.id)}
                    >
                        <ThunderIcon name={d.icon} />
                        <span>{d.label}</span>
                    </button>
                ))}
            </div>
            {/* keyed so each demo starts fresh from its own poster */}
            <DemoVideo key={demo.id} demo={demo} reduceMotion={reduceMotion} />
        </section>
    );
}

function Promo({ inSheet, reduceMotion }) {
    return (
        <article
            className={`thunder-promo ${inSheet ? "thunder-promo--sheet" : ""}`}
            aria-labelledby="thunder-title"
        >
            <header className="thunder-head">
                <img
                    className="thunder-logo"
                    src={LOGO}
                    alt="Thunder Writer logo"
                    width="192"
                    height="192"
                />
                <div>
                    <p className="thunder-badge">
                        <ThunderIcon name="bolt" />
                        Newly released
                    </p>
                    <h1 className="thunder-title" id="thunder-title">Thunder Writer</h1>
                    <p className="thunder-tagline">
                        A <em>free</em> idea processor, not a word processor.
                    </p>
                </div>
            </header>

            <p className="thunder-pitch">
                Write your book on real pages, with a quiet AI editor in the margin.
            </p>

            <a className="thunder-cta" href={APP_URL} target="_blank" rel="noopener noreferrer">
                Open Thunder Writer
                <ThunderIcon name="external" />
                <span className="thunder-sr">(opens in a new tab)</span>
            </a>

            <ul className="thunder-proof">
                {PROOF.map((item) => (
                    <li key={item}>
                        <ThunderIcon name="check" />
                        {item}
                    </li>
                ))}
            </ul>

            <DemoPlayer reduceMotion={reduceMotion} />

            <section aria-labelledby="thunder-features-title">
                <h2 className="thunder-kicker" id="thunder-features-title">What you get</h2>
                <ul className="thunder-features">
                    {FEATURES.map((f) => (
                        <li key={f.title} className="thunder-feature">
                            <span className="thunder-feature-icon">
                                <ThunderIcon name={f.icon} />
                            </span>
                            <span>
                                <strong>{f.title}</strong>
                                {f.text}
                            </span>
                        </li>
                    ))}
                </ul>
            </section>

            <nav className="thunder-links" aria-label="Thunder Writer links">
                <a href={APP_URL} target="_blank" rel="noopener noreferrer">
                    thunderwriter.app
                </a>
                <a href={`${APP_URL}/privacy`} target="_blank" rel="noopener noreferrer">
                    Privacy
                </a>
                <a href={`${APP_URL}/terms`} target="_blank" rel="noopener noreferrer">
                    Terms
                </a>
            </nav>
        </article>
    );
}

function Sheet({ onClose, children }) {
    const sheetRef = useRef(null);

    useEffect(() => {
        const root = document.getElementById("root");
        const opener = document.activeElement;
        // The site behind the sheet goes inert, which keeps keyboard and screen reader focus in here
        if (root) root.inert = true;
        sheetRef.current.focus({ preventScroll: true });

        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);

        return () => {
            document.removeEventListener("keydown", onKey);
            if (root) root.inert = false;
            if (opener && opener.focus) opener.focus({ preventScroll: true });
        };
    }, [onClose]);

    return (
        <div className="thunder-sheet-layer">
            <div className="thunder-sheet-backdrop" onClick={onClose} aria-hidden="true" />
            <div
                ref={sheetRef}
                className="thunder-sheet"
                role="dialog"
                aria-modal="true"
                aria-labelledby="thunder-title"
                tabIndex={-1}
            >
                <div className="thunder-sheet-bar">
                    <button
                        type="button"
                        className="thunder-sheet-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <ThunderIcon name="close" />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

export default function ThunderWriter() {
    const asSheet = useMediaQuery(SHEET_QUERY);
    const reduceMotion = useMediaQuery(REDUCED_MOTION_QUERY);
    const navigate = useNavigate();
    const location = useLocation();

    // Back to wherever the visitor came from; a direct visit lands on Bio, the phone start page
    const close = useCallback(() => {
        if (location.key !== "default") navigate(-1);
        else navigate("/bio", { replace: true });
    }, [location.key, navigate]);

    if (!asSheet) {
        return <Promo reduceMotion={reduceMotion} />;
    }

    return (
        <>
            {/* what the TV shows behind the sheet */}
            <div className="thunder-teaser">
                <img src={LOGO} alt="" width="192" height="192" />
                <span>Thunder Writer</span>
            </div>
            {createPortal(
                <Sheet onClose={close}>
                    <Promo inSheet reduceMotion={reduceMotion} />
                </Sheet>,
                document.body
            )}
        </>
    );
}
